import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { api } from "../api/client"
import { useLanguage } from "../context/LanguageContext"

const TABS = ["learning", "learned", "quiz"]
const SKILLS = ["reading", "writing", "listening", "speaking"]

function sourceLabel(item) {
  return (item.sources || [])
    .map((src) => src.lesson_title || src.skill)
    .filter(Boolean)
    .join(" · ")
}

export default function Vocabulary() {
  const { t } = useLanguage()
  const [tab, setTab] = useState("learning")
  const [stats, setStats] = useState(null)
  const [words, setWords] = useState([])
  const [error, setError] = useState("")
  const [notice, setNotice] = useState("")
  const [loading, setLoading] = useState(true)
  const [pendingId, setPendingId] = useState(null)
  const [quiz, setQuiz] = useState(null)
  const [quizIndex, setQuizIndex] = useState(0)
  const [quizChoice, setQuizChoice] = useState(null)
  const [quizScore, setQuizScore] = useState(0)
  const [quizDone, setQuizDone] = useState(false)
  const [query, setQuery] = useState("")
  const [skillFilter, setSkillFilter] = useState("")

  const refresh = useCallback(async () => {
    setError("")
    try {
      const [nextStats, nextWords] = await Promise.all([
        api.vocabularyStats(),
        api.listVocabulary(),
      ])
      setStats(nextStats)
      setWords(Array.isArray(nextWords) ? nextWords : [])
    } catch (exc) {
      setError(exc.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    refresh()
  }, [refresh])

  const applyLocalStatus = (id, status) => {
    const previousItem = words.find((item) => item.id === id)
    setWords((list) => list.map((item) => (item.id === id ? { ...item, status } : item)))
    setStats((prev) => {
      if (!prev || !previousItem) return prev
      const wasLearned = previousItem.status === "mastered"
      const nowLearned = status === "mastered"
      if (wasLearned === nowLearned) return prev
      const delta = nowLearned ? 1 : -1
      const nextLearned = Math.max(0, (prev.learned ?? prev.mastered ?? 0) + delta)
      const totalCount = prev.total ?? words.length
      return {
        ...prev,
        learned: nextLearned,
        mastered: nextLearned,
        to_learn: Math.max(0, totalCount - nextLearned),
      }
    })
  }

  const setLearnStatus = async (id, status) => {
    if (pendingId) return
    setPendingId(id)
    setError("")
    setNotice("")
    try {
      const updated = await api.markVocabularyStatus(id, status)
      if (updated?.status && updated.status !== status) {
        throw new Error(t("vocabMarkFailed"))
      }
      applyLocalStatus(id, status)
      setNotice(status === "mastered" ? t("vocabMarkedLearned") : t("vocabMarkedLearning"))
      if (status === "mastered") setTab("learned")
      if (status === "learning") setTab("learning")
      await refresh()
    } catch (exc) {
      setError(exc.message || t("vocabMarkFailed"))
    } finally {
      setPendingId(null)
    }
  }

  const startQuiz = async () => {
    setError("")
    setQuizDone(false)
    setQuizChoice(null)
    setQuizIndex(0)
    setQuizScore(0)
    try {
      const data = await api.vocabularyQuiz(5)
      setQuiz(data.questions || [])
    } catch (exc) {
      setError(exc.message)
      setQuiz([])
    }
  }

  const answerQuiz = async (optionIndex) => {
    if (quizChoice !== null || !quiz?.[quizIndex]) return
    const question = quiz[quizIndex]
    setQuizChoice(optionIndex)
    const correct = optionIndex === question.correct_index
    if (correct) setQuizScore((value) => value + 1)
    try {
      await api.reviewVocabulary(question.entry_id, correct ? "good" : "again")
    } catch {
      /* quiz scoring still works if review fails */
    }
  }

  const nextQuiz = async () => {
    if (quizIndex + 1 >= quiz.length) {
      const total = quiz.length
      const score = quizScore
      setQuizDone(true)
      try {
        await api.completeVocabularyQuiz({ score, total })
      } catch {
        /* activity log is best-effort */
      }
      refresh()
      return
    }
    setQuizIndex((value) => value + 1)
    setQuizChoice(null)
  }

  const removeWord = async (id) => {
    if (pendingId) return
    setPendingId(id)
    setError("")
    setNotice("")
    try {
      await api.deleteVocabulary(id)
      setWords((list) => list.filter((item) => item.id !== id))
      await refresh()
    } catch (exc) {
      setError(exc.message)
    } finally {
      setPendingId(null)
    }
  }

  const openSkillWords = (skill) => {
    setSkillFilter(skill)
    setTab("learning")
  }

  const matchesFilters = useCallback(
    (item) => {
      if (skillFilter) {
        const skills = (item.sources || []).map((src) => src.skill)
        if (!skills.includes(skillFilter)) return false
      }
      if (!query.trim()) return true
      const hay = `${item.word} ${item.display_word} ${item.meaning}`.toLowerCase()
      return hay.includes(query.trim().toLowerCase())
    },
    [query, skillFilter]
  )

  const learningWords = useMemo(
    () => words.filter((item) => item.status !== "mastered" && matchesFilters(item)),
    [words, matchesFilters]
  )
  const learnedWords = useMemo(
    () => words.filter((item) => item.status === "mastered" && matchesFilters(item)),
    [words, matchesFilters]
  )

  const total = stats?.total || words.length
  const learned = stats?.learned ?? stats?.mastered ?? 0
  const toLearn = stats?.to_learn ?? Math.max(0, total - learned)
  const progressPct = total > 0 ? Math.min(Math.round((learned / total) * 100), 100) : 0
  const bySkill = stats?.by_skill || {}

  if (loading) {
    return (
      <div className="vocab-page">
        <p className="text-muted-foreground">{t("loading")}</p>
      </div>
    )
  }

  const renderWordList = (items, emptyMessage, learnedList) => {
    if (items.length === 0) {
      return (
        <div className="vocab-empty">
          <p>{emptyMessage}</p>
          {!learnedList && (
            <div className="vocab-empty-links">
              <Link to="/reading">{t("reading")}</Link>
              <Link to="/listening">{t("listening")}</Link>
              <Link to="/speaking">{t("speaking")}</Link>
              <Link to="/writing">{t("writing")}</Link>
            </div>
          )}
        </div>
      )
    }
    return (
      <ul className="vocab-word-list">
        {items.map((item) => {
          const rowBusy = pendingId === item.id
          return (
            <li key={item.id} className={rowBusy ? "is-saving" : undefined} aria-busy={rowBusy}>
              <div>
                <strong>{item.display_word}</strong>
                {item.phonetic ? <p className="vocab-card-phonetic">{item.phonetic}</p> : null}
                <p className="vocab-word-meaning">{item.meaning || t("vocabNoMeaning")}</p>
                {item.example_sentence ? (
                  <p className="vocab-card-example">“{item.example_sentence}”</p>
                ) : null}
                {sourceLabel(item) ? <p className="vocab-card-source">{sourceLabel(item)}</p> : null}
              </div>
              <div className="vocab-word-actions">
                {learnedList ? (
                  <button
                    type="button"
                    className="vocab-btn"
                    disabled={rowBusy}
                    onClick={() => setLearnStatus(item.id, "learning")}
                  >
                    {rowBusy ? t("vocabSaving") : t("vocabKeepLearning")}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="vocab-btn vocab-btn--primary"
                    disabled={rowBusy}
                    onClick={() => setLearnStatus(item.id, "mastered")}
                  >
                    {rowBusy ? t("vocabSaving") : t("vocabMarkLearned")}
                  </button>
                )}
                <button
                  type="button"
                  className="vocab-remove"
                  disabled={rowBusy}
                  onClick={() => removeWord(item.id)}
                >
                  {t("vocabRemove")}
                </button>
              </div>
            </li>
          )
        })}
      </ul>
    )
  }

  return (
    <div className="vocab-page">
      <header className="vocab-hero">
        <h1>{t("vocabulary")}</h1>
        <p>{t("vocabPageLead")}</p>
      </header>

      <div className="vocab-goal">
        <div className="vocab-goal-head">
          <span>{t("vocabProgress")}</span>
          <strong className="tabular-nums">
            {learned}/{total}
          </strong>
        </div>
        <div className="vocab-goal-track" aria-hidden>
          <div className="vocab-goal-fill" style={{ width: `${progressPct}%` }} />
        </div>
        <p className="vocab-goal-hint">
          {t("vocabProgressHint")
            .replace("{learned}", String(learned))
            .replace("{total}", String(total))}
        </p>
      </div>

      <ul className="vocab-stats">
        <li>
          <strong className="tabular-nums">{learned}</strong>
          <span>{t("vocabLearned")}</span>
        </li>
        <li>
          <strong className="tabular-nums">{toLearn}</strong>
          <span>{t("vocabToLearn")}</span>
        </li>
        <li>
          <strong className="tabular-nums">{total}</strong>
          <span>{t("vocabTotal")}</span>
        </li>
        <li>
          <strong className="tabular-nums">{progressPct}%</strong>
          <span>{t("vocabRetention")}</span>
        </li>
      </ul>

      <div className="vocab-skill-row" aria-label={t("vocabBySkill")}>
        {SKILLS.map((skill) => (
          <button
            key={skill}
            type="button"
            className={`vocab-skill-chip ${skillFilter === skill ? "is-active" : ""}`}
            onClick={() => openSkillWords(skill)}
          >
            <span>{t(skill)}</span>
            <strong className="tabular-nums">{bySkill[skill] || 0}</strong>
          </button>
        ))}
      </div>

      <div className="vocab-tabs" role="tablist">
        {TABS.map((id) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            className={`vocab-tab ${tab === id ? "is-active" : ""}`}
            onClick={() => setTab(id)}
          >
            {id === "learning"
              ? `${t("vocabTab_learning")} (${toLearn})`
              : id === "learned"
                ? `${t("vocabTab_learned")} (${learned})`
                : t(`vocabTab_${id}`)}
          </button>
        ))}
      </div>

      {error ? (
        <p className="vocab-inline-error" role="alert">
          {error}
        </p>
      ) : null}
      {notice && !error ? (
        <p className="vocab-inline-ok" role="status">
          {notice}
        </p>
      ) : null}

      {tab !== "quiz" && (
        <section className="vocab-panel">
          <input
            type="search"
            className="vocab-search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t("vocabSearch")}
            aria-label={t("vocabSearch")}
          />
          <div className="vocab-filters">
            <button
              type="button"
              className={`vocab-filter ${!skillFilter ? "is-active" : ""}`}
              onClick={() => setSkillFilter("")}
            >
              {t("vocabFilterAll")}
            </button>
            {SKILLS.map((skill) => (
              <button
                key={skill}
                type="button"
                className={`vocab-filter ${skillFilter === skill ? "is-active" : ""}`}
                onClick={() => setSkillFilter(skill)}
              >
                {t(skill)}
              </button>
            ))}
          </div>
          {tab === "learning"
            ? renderWordList(learningWords, t("vocabEmptyLearning"), false)
            : renderWordList(learnedWords, t("vocabEmptyLearned"), true)}
        </section>
      )}

      {tab === "quiz" && (
        <section className="vocab-panel">
          {!quiz && !quizDone && (
            <div className="vocab-empty">
              <p>{t("vocabQuizLead")}</p>
              <button type="button" className="vocab-btn vocab-btn--primary" onClick={startQuiz}>
                {t("vocabStartQuiz")}
              </button>
            </div>
          )}
          {quiz && quiz.length > 0 && !quizDone && (
            <div className="vocab-card">
              <p className="vocab-card-word">{quiz[quizIndex].display_word}</p>
              <p className="vocab-remaining">
                {quizIndex + 1} / {quiz.length}
              </p>
              <ul className="vocab-quiz-options">
                {quiz[quizIndex].options.map((option, index) => {
                  const chosen = quizChoice === index
                  const correct = index === quiz[quizIndex].correct_index
                  let className = "vocab-quiz-option"
                  if (quizChoice !== null && correct) className += " is-correct"
                  if (chosen && !correct) className += " is-wrong"
                  return (
                    <li key={`${option}-${index}`}>
                      <button
                        type="button"
                        className={className}
                        disabled={quizChoice !== null}
                        onClick={() => answerQuiz(index)}
                      >
                        {option}
                      </button>
                    </li>
                  )
                })}
              </ul>
              {quizChoice !== null && (
                <button type="button" className="vocab-btn vocab-btn--primary" onClick={nextQuiz}>
                  {quizIndex + 1 >= quiz.length ? t("vocabSeeScore") : t("vocabNext")}
                </button>
              )}
            </div>
          )}
          {quizDone && (
            <div className="vocab-empty">
              <p>
                {t("vocabQuizScore")}: {quizScore}/{quiz.length}
              </p>
              <button type="button" className="vocab-btn vocab-btn--primary" onClick={startQuiz}>
                {t("vocabStartQuiz")}
              </button>
            </div>
          )}
        </section>
      )}
    </div>
  )
}
